import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';

import {
  DiagnosticEventType,
  PayloadDepth,
} from '../models/diagnostic-profile';
import { DiagnosticBufferService } from './diagnostic-buffer.service';

const STACK_MAX_CHARS_STANDARD = 2048;
const STACK_TRUNCATION_SUFFIX = '…(truncated)';

function stackForDepth(
  stack: string | undefined,
  depth: PayloadDepth
): string | undefined {
  if (!stack) return undefined;
  switch (depth) {
    case 'minimal':
      return undefined;
    case 'standard':
      return stack.length > STACK_MAX_CHARS_STANDARD
        ? stack.slice(0, STACK_MAX_CHARS_STANDARD) + STACK_TRUNCATION_SUFFIX
        : stack;
    case 'verbose':
      return stack;
  }
}

// Exported for spec coverage only.
export const __test = { stackForDepth };

/**
 * Replaces Angular's default ErrorHandler to capture uncaught errors as
 * DiagnosticErrorEvents in the diagnostic buffer. Stack depth is controlled
 * by the active profile's payloadDepth (EAS-2043):
 *   minimal  → stack omitted
 *   standard → stack truncated to 2048 chars
 *   verbose  → full stack
 *
 * HttpErrorResponse is skipped — the TraceInterceptor already records those
 * as ApiEvents, avoiding duplicate entries in the bundle. Known gap: the
 * TraceInterceptor also skips cross-origin requests (isSameOrigin guard), so
 * a cross-origin HttpErrorResponse will appear neither as an ApiEvent nor as
 * an ErrorEvent in the bundle. This is an accepted trade-off (cross-origin
 * errors are rare in this app and carry third-party context).
 *
 * For promise-rejection / zone-wrapped errors the Angular zone sometimes
 * delivers the cause inside `error.rejection` or `error.ngOriginalError`
 * rather than as the top-level error. We do not unwrap these fields; the
 * `String(error)` fallback covers the degraded case and avoids silent drops.
 *
 * The DiagnosticBufferService is resolved lazily via Injector to avoid a
 * construction-time circular DI cycle (ErrorHandler is instantiated very
 * early in Angular's bootstrap, before the full DI graph is ready).
 *
 * The diagnostic capture is wrapped in try/catch so that any failure in the
 * capture path never suppresses the Angular default error logging.
 */
@Injectable()
export class DiagnosticErrorHandler extends ErrorHandler {
  constructor(private readonly injector: Injector) {
    super();
  }

  override handleError(error: unknown): void {
    this.capture(error);
    super.handleError(error);
  }

  private capture(error: unknown): void {
    if (error instanceof HttpErrorResponse) return;
    try {
      const buffer = this.injector.get(DiagnosticBufferService);
      const depth = buffer.getActiveProfile().payloadDepth;
      const err = error instanceof Error ? error : undefined;
      buffer.push({
        type: DiagnosticEventType.ERROR,
        timestamp: Date.now(),
        // Placeholder for type satisfaction; DiagnosticBufferService.push
        // overrides this with the active profile name.
        profile: 'default',
        payload: {
          message: err?.message ?? String(error),
          source: err?.name,
          stack: stackForDepth(err?.stack, depth),
        },
      });
    } catch {
      // Diagnostic capture failures must never break global error handling.
    }
  }
}
